<template>
  <form class="c-project__versions-form c-form u-mts" @submit.prevent="submit">
    <fieldset class="c-form__fields">
      <div class="c-banner u-mbs">
        <h1 class="c-banner__title">{{ isEdit ? t('editExternalVersion') : t('createNewExternalVersion') }}</h1>
      </div>
      <div class="c-form__field c-field">
        <!-- version number -->
        <div class="c-form__field c-field">
          <div class="c-field__wrapper">
            <label for="version_number" class="c-field__label">
              {{ t('versionNumber') }}
            </label>
            <input
              id="version_number"
              :value="modelValue.versionNumber"
              type="text"
              name="version_number"
              class="c-field__input"
              :disabled="isLoading"
              @input="inputVersion"
            >
            <!-- safe to use here as it's a static string -->
            <!-- eslint-disable-next-line vue/no-v-html -->
            <small class="c-field__hint" v-html="t('versionNumberHint')" />
          </div>
        </div>
        <!-- name -->
        <div class="c-field__wrapper u-mtxs">
          <label for="version_name" class="c-field__label">
            {{ t('name') }}
          </label>
          <input
            id="version-name"
            :value="modelValue.name"
            type="text"
            name="version_name"
            class="c-field__input"
            :disabled="isLoading"
            @input="inputName"
          >
          <!-- safe to use here as it's a static string -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <small class="c-field__hint" v-html="t('nameHint')" />
        </div>
      </div>
      <!-- description -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="version_description" class="c-field__label">
            {{ t('description') }}
          </label>
          <textarea
            id="version_description"
            :value="modelValue.description"
            type="text"
            name="version_description"
            class="c-field__input"
            :disabled="isLoading"
            @input="inputDescripion"
          />
          <!-- safe to use here as it's a static string -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <small class="c-field__hint">{{ t('descriptionHint') }}</small>
        </div>
      </div>
      <!-- date of publication -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="date_published" class="c-field__label">{{ t('publicationDate') }}</label>
          <input
            id="date_published"
            :value="modelValue.datePublished ? modelValue.datePublished.split('T')[0] : ''"
            :disabled="isLoading"
            type="date"
            name="date_published"
            class="c-field__input c-field__input--inline c-field__input--date"
            @input="inputDate"
          >
        </div>
      </div>
      <!-- status -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <span class="c-field__label">{{ t('status') }}</span>
          <label class="c-field__choice">
            <input
              type="radio"
              :disabled="isLoading"
              name="version_status"
              value="0"
              :checked="modelValue.status == '0'"
              class="c-field__input"
              @input="inputStatus"
            >
            <span class="c-field__text">
              {{ t('draft') }}
            </span>
          </label>
          <label class="c-field__choice">
            <input
              type="radio"
              :disabled="isLoading"
              name="version_status"
              value="1"
              :checked="modelValue.status == '1'"
              class="c-field__input"
              @input="inputStatus"
            >
            <span class="c-field__text">
              {{ t('published') }}
            </span>
          </label>
          <label class="c-field__choice">
            <!-- @ts-expect-error -->
            <input
              type="radio"
              :disabled="isLoading"
              name="version_status"
              value="2"
              :checked="modelValue.status == '2'"
              class="c-field__input"
              @input="inputStatus"
            >
            <span class="c-field__text">
              {{ t('archived') }}
              <!-- <div class="u-mtxs">
                <small class="c-field__hint">{{ t('statusHint') }}</small>
              </div> -->
            </span>
          </label>
        </div>
      </div>
      <!-- external url -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="external_url" class="c-field__label">
            {{ t('externalUrl') }}
          </label>
          <input
            id="external_url"
            :value="modelValue.externalUrl"
            type="text"
            name="external_url"
            class="c-field__input c-input_project_external_url"
            :disabled="isLoading"
            required
            @input="inputExternalUrl"
          >
        </div>
      </div>
      <!-- external git repository -->
      <div class="c-form__field c-field">
        <div class="c-field__wrapper">
          <label for="external_git_repo" class="c-field__label">
            {{ t('externalGitRepository') }}
          </label>
          <input
            id="external_git_repo"
            :value="modelValue.externalGitRepository"
            type="text"
            name="external_git_repo"
            class="c-field__input c-input_project_external_git_repository"
            :disabled="isLoading"
            @input="inputExternalGitRepository"
          >
        </div>
      </div>
      <!-- edit versions actions buttons -->
      <fieldset v-if="isEditExternalVersion" class="c-form__actions">
        <Btn ref="versionCancelEditButton" :loading="isLoading" :disabled="isLoading" :label="t('cancel')" class="c-button-cancel cancel-button-versions-create" @click="toggleExternalVersionForm('cancel')" />
        <BtnSubmit ref="versionConfirmEditButton" :loading="isLoading" :disabled="isLoading" :label="isEdit ? t('saveExternalVersion') : t('createExternalVersion')" />
        <!-- delete version popup -->
        <Popover
          trigger="manual"
          :show="confirmDelete"
          theme="danger"
          placement="top"
          @click-outside="confirmDelete = false"
        >
          <template #button>
            <!-- <button
              class="c-button c-button--small c-button--light"
              :disabled="deleteVersion.loading"
              @click.prevent="confirmDeletion"
            >
              <SVGIcon v-if="!deleteVersion.loading" class="c-button__icon" name="trash" />
              <SVGIcon v-else class="c-button__icon" name="spinner-thin" />
              <span class="c-button__label">{{ t('delete') }}</span>
            </button> -->
            <Btn ref="versionDeleteButton" :loading="isLoading" :disabled="isLoading" :label="t('delete')" class="c-button-delete delete-button-versions-edit" @click="confirmDeletion" />
          </template>
          <template #tooltip>
            <span>{{ t('clickAgainToConfirm') }}</span>
          </template>
        </Popover>
      </fieldset>
      <!-- submit -->
      <fieldset v-if="!isEditExternalVersion" class="c-form__actions">
        <div v-if="error" class="c-message c-message--error u-align-center u-mbs">
          <small>{{ error.apiMessage ?? (isEdit ? t('unknownErrorEdit') : t('unknownErrorCreate')) }}</small>
        </div>
        <BtnSubmit :loading="isLoading" :label="isEdit ? t('saveExternalVersion') : t('createExternalVersion')" />
        <!-- safe to use here as it's a static string -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p v-if="!isEdit" class="u-mts c-form__links" v-html="t('createInfo')" />
      </fieldset>
    </fieldset>
  </form>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ProjectExternal, APIError } from '@/utils/api'
import { useI18n } from '@/utils/i18n'
import Btn from '@/components/buttons/Btn.vue'
import BtnSubmit from '@/components/buttons/BtnSubmit.vue'
import Popover from '@/components/Popover.vue'

export interface ExternalVersionForm {
  name: string
  description: string
  versionNumber: string
  datePublished: string
  status: string
  externalUrl: string
  externalGitRepository: string
}

const props = withDefaults(defineProps<{
  modelValue: ExternalVersionForm
  project: ProjectExternal
  versionId?: number
  loading: boolean
  error?: APIError
  isEdit?: boolean
  isEditExternalVersion?: boolean
}>(), { versionId: undefined, error: undefined, isEdit: false, isEditExternalVersion: false })

const emit = defineEmits<{
  (e: 'canceled'): void
  (e: 'update:modelValue', version: ExternalVersionForm): void
  (e: 'deleted'): void
  (e: 'submit'): void
}>()

const project = ref(props.project)
// console.log('PROJECT: ', project.value)

const externalVersion = ref(project.value?.releases)
// const externalVersion = ref(await getProjectExternalVersion(props.versionId))
console.log('EXTERNAL VERSION: ', externalVersion.value)

// version form state
const isLoading = ref(false)

// form actions buttons
const versionCancelButton = ref<HTMLButtonElement>()
const versionConfirmButton = ref<HTMLButtonElement>()
const versionDeleteButton = ref<HTMLButtonElement>()

const versionCancelEditButton = ref<HTMLButtonElement>()
const versionConfirmEditButton = ref<HTMLButtonElement>()

const versionCancelButtonDisabled = ref(false)
const versionConfirmButtonDisabled = ref(false)
// const versionDeleteButtonDisabled = ref(false)
// const versionCancelEditButtonDisabled = ref(false)
// const versionConfirmEditButtonDisabled = ref(false)

// Versions input events
function inputName (event: Event) {
  const name = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, name })
}

function inputDescripion (event: Event) {
  const description = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, description })
}

function inputVersion (event: Event) {
  const versionNumber = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, versionNumber })
}

function inputDate (event: Event) {
  const datePublished = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, datePublished })
}

function inputStatus (event: Event) {
  const status = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, status })
}

function inputExternalUrl (event: Event) {
  const externalUrl = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, externalUrl })
}

function inputExternalGitRepository (event: Event) {
  const externalGitRepository = (event.currentTarget as HTMLInputElement).value
  emit('update:modelValue', { ...props.modelValue, externalGitRepository })
}

function toggleExternalVersionForm (action: string) {
  if (action === 'cancel') {
    emit('canceled')
  }
}

onMounted(() => {
  // init form
  if (versionCancelButton.value) {
    versionCancelButtonDisabled.value = false
  }

  if (versionConfirmButton.value) {
    versionConfirmButtonDisabled.value = false
  }

  // if (versionDeleteButton.value) {
  //   versionDeleteButtonDisabled.value = false
  // }

  // if (versionCancelEditButton.value) {
  //   versionCancelEditButtonDisabled.value = false
  // }

  // if (versionConfirmEditButton.value) {
  //   versionConfirmEditButtonDisabled.value = false
  // }
})

const confirmDelete = ref(false)

function confirmDeletion () {
  // ask user confirmation
  if (confirmDelete.value === false) {
    confirmDelete.value = true
    return
  }
  confirmDelete.value = false

  isLoading.value = true

  // disable actions buttons
  if (versionCancelEditButton.value) {
    versionCancelButtonDisabled.value = true
  }

  // if (versionConfirmEditButton.value) {
  //   versionConfirmEditButtonDisabled.value = true
  // }

  // if (versionDeleteButton.value) {
  //   versionDeleteButtonDisabled.value = true
  // }

  emit('deleted')
}

async function submit () {
  isLoading.value = true

  emit('submit')
}

const { t } = useI18n({
  messages: {
    en: {
      createNewExternalVersion: 'Create an external version',

      name: 'Name',
      nameHint: '(required) Usually a version number. Have you heard about <a href="https://semver.org/">semver</a>?',

      description: 'Description',
      descriptionHint: 'Displayed below the version name in most case.',

      versionNumber: 'Version number',
      versionNumberHint: 'The release version number, preferably using <a href="https://semver.org/" target="_blank">semantic versioning.</a>',

      publicationDate: 'Date of publication',

      status: 'Status',
      statusHint: 'Makes the project accessible by you only and prevents it from showing up in collections and search results. The repository and the discussion room will still be accessible to the public.',

      externalUrl: 'External url',

      externalGitRepository: 'External git repository',

      published: 'Published',
      draft: 'Draft',
      archived: 'Archived',

      platform: 'Platform',

      premium: 'Premium access',

      unknownErrorCreate: 'An error occurred while trying to create the version, try again. If this keeps happening, please contact us.',
      unknownErrorEdit: 'An error occurred while trying to edit the version, try again. If this keeps happening, please contact us.',

      createExternalVersion: 'Create the version',
      createInfo: `
        Please consider the version public once created.<br>
        Subscribers will receive an e-mail alert about the new version.
      `,

      editExternalVersion: 'Edit version',
      saveExternalVersion: 'Save version',

      clickAgainToConfirm: 'Click again to confirm',

      save: 'Save and add asset',
      cancel: 'Cancel',
      delete: 'Delete'
    },
    fr: {
      createNewExternalVersion: 'Créer une version externe',

      name: 'Nom',
      nameHint: '',

      description: 'Description',
      descriptionHint: 'Affiché sous le nom de la release',

      versionNumber: 'Numéro de version',
      versionNumberHint: 'Numéro de version de la release selon un <a href="https://semver.org/">schéma sémantique</a> de préférence.',

      publicationDate: 'Date de publication',

      status: 'Statut',
      statusHint: "Rends le projet accessible par vous uniquement et l'empêche d'apparaître dans les collections et les résultats de recherche. Le dépôt et le salon de discussion seront toujours accessibles.",

      externalUrl: 'Url externe',

      externalGitRepository: 'Dépôt git externe',

      published: 'Publié',
      draft: 'Brouillon',
      archived: 'Archivé',

      platform: 'Platforme',

      premium: 'Accès premium',

      unknownErrorCreate: 'Une erreur est survenue lors de la création de la version, merci de réessayer. Si le problème se reproduit, merci de nous contacter.',
      unknownErrorEdit: 'Une erreur est survenue l\'édition de la version, merci de réessayer.Si le problème se reproduit, merci de nous contacter.',

      createExternalVersion: 'Créer la version',
      createInfo: `
        Merci de considérer que la version est publique une fois créée.<br>
        Les personnes ayant souscris aux alertes du projet recevront un e-mail
        à propos de la nouvelle version.
      `,

      editExternalVersion: 'Modifier une version',
      saveExternalVersion: 'Edition de version',

      clickAgainToConfirm: 'Re-cliquer pour confirmer',

      save: 'Enregistrer et ajouter un asset',
      cancel: 'Annuler',
      delete: 'Supprimer'
    }
  }
})
</script>
